import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useRoomSocket } from '../lib/socket';
import type { LobbyView, PlayerPublic, PrivateState } from '@shum/shared';
import { QrCode } from '../components/QrCode';
import { ReconnectButton } from '../components/ReconnectButton';
import { AudioManager } from '../components/AudioManager';
import { GameScreens } from '../game/screens';
import { RoomStyles } from '../game/styles';

interface RoomSummary extends LobbyView {
  phase: string;
  dayNumber: number;
  winner: string | null;
}

export function Room() {
  const { code } = useParams<{ code: string }>();
  const [summary, setSummary] = useState<RoomSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const { state, send, selectPlayer, reclaim, clearError, reconnect } = useRoomSocket(code);

  useEffect(() => {
    if (!code) return;
    setSummary(null);
    setSummaryError(null);
    api
      .getRoom(code.toUpperCase())
      .then((s) => setSummary(s as RoomSummary))
      .catch((e) => setSummaryError((e as Error).message));
  }, [code]);

  // Prefer live lobby data from the socket, fall back to summary.
  const players: PlayerPublic[] = state.lobby?.players ?? summary?.players ?? [];
  const joinUrl = `${window.location.origin}/r/${summary?.code ?? code}`;
  const qrPayload = joinUrl;
  const canStart = state.lobby?.canStart ?? summary?.canStart ?? false;
  const phase = state.phase?.phase ?? (summary?.phase as PrivateState['phase']) ?? 'LOBBY';
  const selectedId = state.selectedPlayerId;
  const me = players.find((p) => p.playerId === selectedId) ?? null;
  const isDeadScreen = state.privateState?.screen === 'dead';
  const showTopbar = !isDeadScreen || Boolean(me?.isCreator);
  const meIsAlive = state.privateState?.isAlive ?? me?.isAlive ?? true;
  const canCurrentPlayerStart = Boolean(me?.isCreator && canStart);
  const inGame = Boolean(me) && phase !== 'LOBBY';
  const disconnected = state.status === 'closed';
  const audioEnabled = Boolean(me?.isCreator && (summary?.settings?.audioEnabled ?? true));

  let content: React.ReactNode = null;

  if (summaryError) {
    content = (
      <div className="big-error">
        <span className="label">Комната не найдена</span>
        <h1>Такой комнаты нет</h1>
        <p>{summaryError}</p>
        <Link to="/play/mafia/create" className="btn-primary lg">
          Создать новую →
        </Link>
      </div>
    );
  } else if (!summary) {
    content = (
      <div className="connecting">
        <span className="label">Подключаемся…</span>
        <div className="spinner" />
      </div>
    );
  } else if (state.replaced) {
    const canReclaim = Boolean(state.replaced.canReclaim);
    content = (
      <div className="big-error">
        <span className="label">Сессия перенесена</span>
        <h1>Ваш игрок открыт на другом устройстве</h1>
        {canReclaim ? (
          <>
            <p>Кто-то выбрал ваше имя. Если это не вы — верните себя, а перехватчика выкинем.</p>
            <button
              className="btn-primary lg"
              onClick={() => {
                if (!reclaim()) alert('Нет связи с комнатой. Попробуйте ещё раз.');
              }}
            >
              Нет, вернуть меня
            </button>
            <button className="btn-ghost" onClick={() => clearError()}>
              Выбрать другое имя
            </button>
          </>
        ) : (
          <>
            <p>Выберите себя заново на этом устройстве, чтобы продолжить.</p>
            <button className="btn-primary lg" onClick={() => clearError()}>
              Понятно
            </button>
          </>
        )}
        {state.error && <div className="err">{state.error.message}</div>}
        {disconnected && <div className="banner">Нет связи. Переподключаемся…</div>}
      </div>
    );
  } else if (!me) {
    content = (
      <div className="id-sel">
        <span className="label">Комната {summary.code}</span>
        <h1>Выберите своё имя</h1>
        <div className="name-grid">
          {players.map((p) => (
            <button
              key={p.playerId}
              className="name-btn"
              onClick={() => selectPlayer(p.playerId)}
              data-status={p.status}
            >
              <span className="name">{p.name}</span>
              <span className="status">
                {p.status === 'connected' ? 'переподключиться' : p.status === 'disconnected' ? 'отключился' : 'войти'}
              </span>
              {p.isCreator && <span className="star">★</span>}
            </button>
          ))}
        </div>
        {state.error && <div className="err">{state.error.message}</div>}
        {disconnected && <div className="banner">Нет связи. Переподключаемся…</div>}
      </div>
    );
  } else if (phase === 'LOBBY') {
    content = (
      <div className="lobby">
        <div className="lobby-left">
          <span className="label">Лобби · ждём игроков</span>
          <h1>Комната «{summary.code}»</h1>
          <p className="muted">Покажите этот QR или отправьте ссылку в чат.</p>
          <QrCode value={qrPayload} />
          <div className="join-url mono">{joinUrl}</div>
        </div>
        <div className="lobby-right">
          <div className="panel">
            <div className="panel-head">
              <span className="label">Игроки · {players.length}</span>
              <span className={`pill ${canStart ? 'on' : ''}`}>
                <span className="dot" />
                {canStart ? 'все на месте' : 'ждём…'}
              </span>
            </div>
            <div className="lobby-list">
              {players.map((p) => (
                <div className="lobby-player" key={p.playerId} data-status={p.status}>
                  <span className="lp-name">
                    {p.name} {p.isCreator && <span className="star">★</span>}
                  </span>
                  <span className="lp-status">{statusLabel(p.status)}</span>
                </div>
              ))}
            </div>
            {me?.isCreator && (
              <button
                className="btn-primary block lg"
                disabled={!canCurrentPlayerStart}
                onClick={() => {
                  if (!send({ type: 'room.start' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
                }}
              >
                {canStart ? 'Начать игру →' : 'Ждём подключения всех'}
              </button>
            )}
            {!canStart && <p className="hint">Нужно, чтобы все выбрали имя. Кто-то отключился — переподключится через QR.</p>}
            {canStart && !me?.isCreator && <p className="hint">Старт доступен только отмеченному ведущему.</p>}
            {state.error && <div className="err">{state.error.message}</div>}
          </div>
        </div>
        {disconnected && <div className="banner">Нет связи. Переподключаемся…</div>}
      </div>
    );
  } else {
    // In-game.
    content = (
      <div className="game">
        {disconnected && <div className="banner">Нет связи. Переподключаемся…</div>}
        {showTopbar && (
          <div className="topbar">
            <span className="label">День {state.privateState?.dayNumber ?? summary.dayNumber}</span>
            <span className="label">{phaseLabel(phase)}</span>
            {me && (
              <span className="pill" data-alive={meIsAlive ? '1' : '0'}>
                {me.name}
              </span>
            )}
            {me?.isCreator && canAdvanceManually(phase) && (
              <button
                className="btn-ghost top-action"
                onClick={() => {
                  if (!send({ type: 'room.advance' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
                }}
              >
                Дальше
              </button>
            )}
          </div>
        )}
        <GameScreens
          state={state}
          send={send}
          privateState={state.privateState}
          roomId={summary.code}
          isCreator={Boolean(me?.isCreator)}
          settings={summary.settings}
        />
      </div>
    );
  }

  return (
    <div className="room-root">
      <ReconnectButton status={state.status} onReconnect={reconnect} />
      <Header inGame={inGame} onShowQr={() => setShowQr(true)} />
      <RoomStyles />
      {summary && <AudioManager audio={state.audio} enabled={audioEnabled} />}
      {content}
      {showQr && summary && (
        <QrOverlay joinUrl={joinUrl} value={qrPayload} onClose={() => setShowQr(false)} />
      )}
    </div>
  );
}

function Header({ inGame, onShowQr }: { inGame: boolean; onShowQr: () => void }) {
  return (
    <header className="bar">
      <div className="bar-row" style={{ padding: '14px 32px' }}>
        <Link to="/" className="logo">
          <span className="logo-mark">Ш</span>
          <span className="logo-name">Шум.клуб</span>
        </Link>
        {inGame ? (
          <button
            type="button"
            className="label"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontFamily: 'inherit',
              fontSize: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
            }}
            onClick={onShowQr}
          >
            Показать QR код
          </button>
        ) : (
          <Link to="/rooms" className="label" style={{ textDecoration: 'none' }}>
            Войти по коду
          </Link>
        )}
      </div>
    </header>
  );
}

function QrOverlay({ joinUrl, value, onClose }: { joinUrl: string; value: string; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(24,20,16,.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-card)',
          padding: '28px 24px',
          maxWidth: 360,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          boxShadow: '0 20px 80px rgba(0,0,0,.4)',
        }}
      >
        <span className="label">Подключиться в комнату</span>
        <QrCode value={value} />
        <div className="join-url mono" style={{ textAlign: 'center' }}>
          {joinUrl}
        </div>
        <p className="muted" style={{ textAlign: 'center', margin: 0 }}>
          Покажите код — или отправьте ссылку в чат.
        </p>
        <button className="btn-primary lg block" onClick={onClose}>
          Вернуться
        </button>
      </div>
    </div>
  );
}

function statusLabel(s: PlayerPublic['status']): string {
  switch (s) {
    case 'connected':
      return 'в игре';
    case 'disconnected':
      return 'отключился';
    case 'replaced':
      return 'заменён';
    default:
      return 'не вошёл';
  }
}

function phaseLabel(phase: PrivateState['phase']): string {
  const map: Record<string, string> = {
    ROLE_REVEAL: 'Раздача ролей',
    NIGHT_START: 'Город засыпает',
    MAFIA_ACTION: 'Ход мафии',
    MAFIA_DON_ACTION: 'Ход Дона мафии',
    DOCTOR_ACTION: 'Ход доктора',
    DETECTIVE_ACTION: 'Ход комиссара',
    NIGHT_SLEEP: 'Город спит',
    NIGHT_RESOLUTION: 'Утро',
    DAY_START: 'День',
    DISCUSSION: 'Высказывание мнения',
    VOTING: 'Голосование',
    DAY_RESOLUTION: 'Подсчёт голосов',
    TIEBREAK: 'Переголосование',
    LAST_WORDS: 'Последнее слово',
    GAME_OVER: 'Игра окончена',
  };
  return map[phase] ?? phase;
}

function canAdvanceManually(phase: PrivateState['phase']): boolean {
  return ![
    'LOBBY',
    'MAFIA_ACTION',
    'MAFIA_DON_ACTION',
    'DOCTOR_ACTION',
    'DETECTIVE_ACTION',
    'TIEBREAK',
    'LAST_WORDS',
    'GAME_OVER',
  ].includes(phase);
}
